import api from './api';
import { getSalesStats, getMonthlySales, getTopProducts, getAtRiskOpportunities } from './dashboardApi';
import type { DashboardData } from '../types/dashboard';

// Dashboard layout API
export const dashboardLayoutApi = {
  getMyDefault: async (): Promise<DashboardData> => {
    const response = await api.get('/analytics/dashboards/my-default/');
    return response.data;
  },
  saveLayout: async (id: number, layout: any): Promise<DashboardData> => {
    const response = await api.patch(`/analytics/dashboards/${id}/save-layout/`, { layout });
    return response.data;
  },
};

// Simple request cache to avoid duplicate calls in the same render cycle
const requestCache = new Map<string, { promise: Promise<any>; timestamp: number }>();
const CACHE_TTL = 5000; // 5 seconds

function cachedFetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const cached = requestCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.promise as Promise<T>;
  }
  const promise = fetcher();
  requestCache.set(key, { promise, timestamp: Date.now() });
  promise.catch(() => requestCache.delete(key));
  return promise;
}

// Widget data fetchers
export const widgetFetchers: Record<string, (config: Record<string, any>, userId?: number) => Promise<any>> = {
  // KPI Widgets
  kpi_deals_value: async (_config, userId) => {
    return cachedFetch(`deals_${userId}`, async () => {
      const res = await api.get(`/deals/deals/?owner=${userId}&status=open`);
      const deals = res.data.results || res.data || [];
      const totalValue = deals.reduce((sum: number, d: any) => sum + (parseFloat(d.total_value) || 0), 0);
      return { value: totalValue, count: deals.length, label: 'em negociacoes abertas' };
    });
  },

  kpi_conversations: async () => {
    return cachedFetch('conversations', async () => {
      const res = await api.get('/messaging/conversations/?ordering=-last_message_at&limit=50');
      const conversations = res.data.results || res.data || [];
      const unread = conversations.filter((c: any) => c.unread_count > 0).length;
      const awaiting = conversations.filter((c: any) => c.last_message_direction === 'outbound' || c.unread_count > 0).length;
      return { value: awaiting, unread, total: conversations.length, label: 'aguardando resposta' };
    });
  },

  kpi_pending_tasks: async () => {
    return cachedFetch('activities_dashboard', async () => {
      const res = await api.get('/activities/activities/dashboard/');
      return { value: res.data.upcoming || 0, completedToday: res.data.completed_today || 0, label: 'tarefas pendentes' };
    });
  },

  kpi_overdue_tasks: async () => {
    return cachedFetch('activities_dashboard', async () => {
      const res = await api.get('/activities/activities/dashboard/');
      return { value: res.data.overdue || 0, label: 'tarefas atrasadas' };
    });
  },

  kpi_total_sales: async () => {
    return cachedFetch('sales_stats', async () => {
      const stats = await getSalesStats();
      return { value: stats.total_sales, wonDeals: stats.won_deals, label: 'vendas totais' };
    });
  },

  kpi_conversion_rate: async () => {
    return cachedFetch('sales_stats', async () => {
      const stats = await getSalesStats();
      return { value: stats.conversion_rate, winRate: stats.win_rate, label: 'taxa de conversao' };
    });
  },

  kpi_leads_today: async () => {
    return cachedFetch('analytics_overview', async () => {
      const res = await api.get('/analytics/overview/');
      return { value: res.data.leads.today, thisMonth: res.data.leads.this_month, total: res.data.leads.total, label: 'leads hoje' };
    });
  },

  // Chart Widgets
  chart_monthly_sales: async (config) => {
    const year = config.year || new Date().getFullYear();
    return cachedFetch(`monthly_sales_${year}`, () => getMonthlySales({ year }));
  },

  chart_conversion_funnel: async (config) => {
    const pipelineId = config.pipeline_id;
    if (!pipelineId) {
      // Get first pipeline
      const pRes = await api.get('/pipelines/');
      const pipelines = pRes.data.results || pRes.data || [];
      if (pipelines.length === 0) return { stage_data: [] };
      const pid = pipelines[0].id;
      const res = await api.get(`/dashboard/stage_conversion/`, { params: { pipeline_id: pid } });
      return res.data;
    }
    const res = await api.get(`/dashboard/stage_conversion/`, { params: { pipeline_id: pipelineId } });
    return res.data;
  },

  chart_leads_trend: async (config) => {
    const period = config.period || 30;
    return cachedFetch(`chart_leads_${period}`, async () => {
      const res = await api.get(`/analytics/charts/`, { params: { type: 'leads', period } });
      return res.data;
    });
  },

  chart_revenue_trend: async (config) => {
    const period = config.period || 30;
    return cachedFetch(`chart_revenue_${period}`, async () => {
      const res = await api.get(`/analytics/charts/`, { params: { type: 'revenue', period } });
      return res.data;
    });
  },

  chart_top_products: async (config) => {
    const limit = config.limit || 10;
    return cachedFetch(`top_products_${limit}`, () => getTopProducts({ limit }));
  },

  // List Widgets
  list_recent_deals: async (_config, userId) => {
    return cachedFetch(`recent_deals_${userId}`, async () => {
      const res = await api.get(`/deals/deals/?owner=${userId}&status=open&ordering=-updated_at&limit=5`);
      const deals = res.data.results || res.data || [];
      return deals.slice(0, 5).map((d: any) => ({
        id: d.id, name: d.name, lead_name: d.lead_name || 'Sem lead',
        stage_name: d.stage_name || 'Sem etapa', stage_color: d.stage_color || '#6366f1',
        total_value: parseFloat(d.total_value) || 0, updated_at: d.updated_at,
      }));
    });
  },

  list_recent_activities: async () => {
    return cachedFetch('activities_dashboard', async () => {
      const res = await api.get('/activities/activities/dashboard/');
      return (res.data.recent || []).slice(0, 5).map((a: any) => ({
        id: a.id, title: a.title, activity_type: a.activity_type,
        scheduled_at: a.scheduled_at, lead_name: a.lead_name, deal_name: a.deal_name,
      }));
    });
  },

  list_recent_conversations: async () => {
    return cachedFetch('recent_conversations', async () => {
      const res = await api.get('/messaging/conversations/?ordering=-last_message_at&limit=10');
      const conversations = res.data.results || res.data || [];
      return conversations.filter((c: any) => c.last_message_direction === 'outbound' || c.unread_count > 0)
        .slice(0, 5).map((c: any) => ({
          id: c.id, contact_name: c.contact_name || 'Contato', contact_avatar: c.contact_avatar,
          last_message: c.last_message_preview, last_message_at: c.last_message_at, unread_count: c.unread_count || 0,
        }));
    });
  },

  list_at_risk_opportunities: async (config) => {
    const days = config.days_without_activity || 7;
    return cachedFetch(`at_risk_${days}`, async () => {
      const data = await getAtRiskOpportunities({ days_without_activity: days });
      return data.opportunities || [];
    });
  },

  // Calendar Widget
  mini_calendar: async () => {
    return cachedFetch('activities_list', async () => {
      const res = await api.get('/activities/activities/?ordering=start_date&limit=50');
      const activities = res.data.results || res.data || [];
      return activities;
    });
  },
};
